type Props = {
  language?: "en" | "zh";
};

/**
 * End User License Agreement tab (English + Simplified Chinese), replicated from
 * the icheck-chinese project. Re-mounts on `language` to replay the enter
 * animation via tailwindcss-animate.
 */
const EULATab = ({ language = "en" }: Props) => {
  return (
    <div
      key={language}
      className="animate-in fade-in slide-in-from-right-10 duration-500 ease-in-out"
    >
      {language === "zh" ? <ChineseContent /> : <EnglishContent />}
    </div>
  );
};

const EnglishContent = () => (
  <>
    <h1 className="text-2xl font-bold mb-4">
      End User License Agreement (EULA)
    </h1>
    <p className="mb-4">Effective Date: 22-August-2025</p>
    <h2 className="text-xl font-semibold mb-2">1. Introduction</h2>
    <p className="mb-4">
      This End User License Agreement ("EULA") is a legal agreement between
      you ("User") and iCheck ("Service Provider") for the use of the iCheck
      compliance and inspection management system ("Platform") and associated
      software. By accessing or using the Platform, you agree to be bound by
      the terms of this EULA.
    </p>
    <h2 className="text-xl font-semibold mb-2">2. License Grant</h2>
    <p className="mb-4">
      iCheck grants you a non-exclusive, non-transferable license to use the
      Platform and associated software for personal or organizational
      compliance and inspection purposes in accordance with this EULA.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      3. Account Registration and Security
    </h2>
    <p>
      <strong>Account Information:</strong> Users must provide accurate and
      complete information during registration.
    </p>
    <p className="mb-4">
      <strong>Account Security:</strong> Users are responsible for maintaining
      the confidentiality of their account credentials and for all activities
      that occur under their account.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      4. Permitted Uses and Restrictions
    </h2>
    <ul className="mb-4">
      <li>
        Modify, copy, or create derivative works based on the Platform or its
        content.
      </li>
      <li>
        Distribute, lease, or sublicense the Platform to any third party.
      </li>
      <li>
        Reverse engineer, decompile, or disassemble any part of the Platform.
      </li>
      <li>
        Use the Platform to conduct any illegal activities or to violate the
        rights of others.
      </li>
    </ul>
    <h2 className="text-xl font-semibold mb-2">
      5. Intellectual Property Rights
    </h2>
    <p>
      All intellectual property rights in the Platform and its content are
      owned by iCheck or its licensors.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>6. Payment Terms</strong>
    </h2>
    <p className="mb-4">
      Users agree to pay all applicable fees for access to the Platform and
      its features.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>7. Data Protection and Privacy</strong>
    </h2>
    <p className="mb-4">
      iCheck is committed to protecting user data in accordance with
      applicable data protection laws.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>8. Confidentiality</strong>
    </h2>
    <p className="mb-4">
      Users agree to keep confidential any proprietary information received
      through the Platform and not disclose it to third parties.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>9. Termination</strong>
    </h2>
    <p className="mb-4">
      This EULA is effective until terminated. iCheck may terminate or suspend
      user accounts for violations of this EULA.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>10. Warranty Disclaimer</strong>
    </h2>
    <p className="mb-4">
      The Platform is provided "as is" without warranties of any kind. iCheck
      does not warrant that the Platform will meet user requirements or be
      error-free.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>11. Limitation of Liability</strong>
    </h2>
    <p className="mb-4">
      iCheck is not liable for any indirect, incidental, or consequential
      damages arising from the use of the Platform.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>12. Governing Law</strong>
    </h2>
    <p className="mb-4">
      This EULA is governed by the laws of India. Any disputes will be
      resolved in the courts of Mumbai, India.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>13. Dispute Resolution</strong>
    </h2>
    <p className="mb-4">
      Any dispute, controversy, or claim arising out of or relating to this
      EULA shall be resolved by arbitration in accordance with the Indian
      Arbitration and Conciliation Act 1996.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>14. Amendments</strong>
    </h2>
    <p className="mb-4">
      iCheck may amend this EULA by providing notice to users. Continued use
      of the Platform constitutes acceptance of the revised EULA.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>15. Severability</strong>
    </h2>
    <p className="mb-4">
      If any provision of this EULA is found to be invalid or unenforceable,
      the remaining provisions will remain in full force and effect.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>16. Entire Agreement</strong>
    </h2>
    <p className="mb-4">
      This EULA constitutes the entire agreement between the User and iCheck
      regarding the use of the Platform.
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>17. Contact Information</strong>
    </h2>
    <p className="mb-4">
      For any questions regarding this EULA, please contact us at{" "}
      <a href="mailto:support@elementree.co.in">support@elementree.co.in</a>.
    </p>
  </>
);

const ChineseContent = () => (
  <>
    <h1 className="text-2xl font-bold mb-4">最终用户许可协议(EULA)</h1>
    <p className="mb-4">生效日期:2025 年 8 月 22 日</p>
    <h2 className="text-xl font-semibold mb-2">1. 引言</h2>
    <p className="mb-4">
      本最终用户许可协议("EULA")是您("用户")与 iCheck("服务提供商")之间就使用 iCheck 合规与检查管理系统("平台")及相关软件所订立的法律协议。访问或使用本平台即表示您同意受本 EULA 条款的约束。
    </p>
    <h2 className="text-xl font-semibold mb-2">2. 许可授予</h2>
    <p className="mb-4">
      iCheck 授予您一项非独占的、不可转让的许可,以根据本 EULA 出于个人或组织合规与检查目的使用本平台及相关软件。
    </p>
    <h2 className="text-xl font-semibold mb-2">3. 账户注册与安全</h2>
    <p>
      <strong>账户信息:</strong> 用户在注册时必须提供准确完整的信息。
    </p>
    <p className="mb-4">
      <strong>账户安全:</strong>{" "}
      用户负责保密其账户凭据,并对其账户下发生的所有活动负责。
    </p>
    <h2 className="text-xl font-semibold mb-2">4. 许可使用与限制</h2>
    <ul className="mb-4">
      <li>修改、复制或基于本平台或其内容创建衍生作品。</li>
      <li>将本平台分发、出租或再许可给任何第三方。</li>
      <li>对本平台的任何部分进行逆向工程、反编译或反汇编。</li>
      <li>使用本平台从事任何非法活动或侵犯他人权利。</li>
    </ul>
    <h2 className="text-xl font-semibold mb-2">5. 知识产权</h2>
    <p>
      本平台及其内容中的所有知识产权归 iCheck 或其许可人所有。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>6. 付款条款</strong>
    </h2>
    <p className="mb-4">
      用户同意支付访问本平台及其功能所应付的所有费用。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>7. 数据保护与隐私</strong>
    </h2>
    <p className="mb-4">
      iCheck 承诺根据适用的数据保护法律保护用户数据。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>8. 保密</strong>
    </h2>
    <p className="mb-4">
      用户同意对通过本平台收到的任何专有信息予以保密,并不向第三方披露。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>9. 终止</strong>
    </h2>
    <p className="mb-4">
      本 EULA 自生效之日起持续有效,直至终止。对于违反本 EULA 的行为,iCheck 可终止或暂停用户账户。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>10. 保证免责声明</strong>
    </h2>
    <p className="mb-4">
      本平台按"现状"提供,不附带任何形式的保证。iCheck 不保证本平台将满足用户要求或无错误。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>11. 责任限制</strong>
    </h2>
    <p className="mb-4">
      iCheck 对因使用本平台而产生的任何间接、附带或后果性损害不承担责任。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>12. 管辖法律</strong>
    </h2>
    <p className="mb-4">
      本 EULA 受印度法律管辖。任何争议将在印度孟买的法院解决。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>13. 争议解决</strong>
    </h2>
    <p className="mb-4">
      因本 EULA 引起的或与之相关的任何争议、纠纷或索赔,应根据 1996 年《印度仲裁与调解法》通过仲裁解决。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>14. 修订</strong>
    </h2>
    <p className="mb-4">
      iCheck 可以通过向用户发出通知的方式修订本 EULA。继续使用本平台即构成对修订后 EULA 的接受。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>15. 可分割性</strong>
    </h2>
    <p className="mb-4">
      若本 EULA 的任何条款被认定为无效或不可执行,其余条款仍将完全有效。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>16. 完整协议</strong>
    </h2>
    <p className="mb-4">
      本 EULA 构成用户与 iCheck 之间就本平台使用的完整协议。
    </p>
    <h2 className="text-xl font-semibold mb-2">
      <strong>17. 联系信息</strong>
    </h2>
    <p className="mb-4">
      如对本 EULA 有任何疑问,请通过{" "}
      <a href="mailto:support@elementree.co.in">support@elementree.co.in</a> 与我们联系。
    </p>
  </>
);

export default EULATab;
